import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DateInputWithArrows from '../../components/DateInputWithArrows';
import { formatDateEuropean } from '../../utils/dateUtils';
import { canAccess } from '../../utils/permissions';
import { UNITS } from '../../constants/units';

export function unitSelectOptions(current) {
  const values = Object.values(UNITS);
  if (current && !values.includes(current)) return [...values, current];
  return values;
}

export const TYPES = [
  { type: 'QUOTE', path: '/finance/quotes', read: 'finance.quotes.read', write: 'finance.quotes.write' },
  { type: 'DELIVERY_NOTE', path: '/finance/deliveries', read: 'finance.deliveries.read', write: 'finance.deliveries.write' },
  { type: 'INVOICE', path: '/finance/invoices', read: 'finance.invoices.read', write: 'finance.invoices.write' },
];

export function emptyFinanceLine(type, projectId = '') {
  return {
    description: '',
    quantity: '',
    unit: 'kom',
    unitPrice: '',
    totalAmount: '',
    costKind: type === 'DELIVERY_NOTE' ? 'PROJECT' : 'DIRECT',
    projectId: projectId || '',
  };
}

export function emptyForm(type) {
  return {
    type,
    documentNumber: '',
    documentDate: new Date().toISOString().slice(0, 10),
    companyId: null,
    counterpartyName: '',
    currency: 'EUR',
    notes: '',
    confirmDuplicate: false,
    confirmUnassigned: false,
    quote: { validUntil: '', expectedDeliveryDate: '', netAmount: '', taxAmount: '', totalAmount: '', status: 'DRAFT' },
    deliveryNote: { receivedAt: new Date().toISOString().slice(0, 10) },
    invoice: {
      direction: 'INCOMING',
      categoryId: '',
      subcategoryId: '',
      issueDate: new Date().toISOString().slice(0, 10),
      dueDate: '',
      netAmount: '',
      taxAmount: '',
      totalAmount: '',
    },
    lines: [emptyFinanceLine(type)],
    paid: false,
    relatedDocuments: [],
    attachmentFile: null,
    lineTotalAcknowledged: null,
  };
}

export function documentLabel(doc) {
  if (!doc) return '';
  const number = doc.documentNumber || `#${doc.id}`;
  return `${number} · ${doc.counterpartyName} · ${formatDateEuropean(doc.documentDate)}`;
}

function lineIsValid(line) {
  return Boolean(String(line.description || '').trim());
}

export function getDocumentTotal(form, type) {
  if (type === 'QUOTE') return Number(form.quote?.totalAmount);
  if (type === 'INVOICE') return Number(form.invoice?.totalAmount);
  return NaN;
}

export function getLineSum(form) {
  return (form.lines || []).reduce((sum, line) => {
    if (!String(line.description || '').trim()) return sum;
    const amount = Number(line.totalAmount);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

export function lineTotalCheck(form, type) {
  if (type !== 'QUOTE' && type !== 'INVOICE') return { status: 'ok' };
  const docTotal = getDocumentTotal(form, type);
  const lineSum = getLineSum(form);
  if (!Number.isFinite(docTotal) || !Number.isFinite(lineSum)) return { status: 'ok', lineSum, docTotal };
  if (Math.abs(lineSum - docTotal) <= 0.01) return { status: 'ok', lineSum, docTotal };
  if (lineSum > docTotal && lineSum > 0) {
    const percent = Math.round((1 - docTotal / lineSum) * 10000) / 100;
    return { status: 'discount', lineSum, docTotal, percent };
  }
  return { status: 'short', lineSum, docTotal };
}

export function isLineTotalAcknowledged(form, type) {
  const ack = form.lineTotalAcknowledged;
  const check = lineTotalCheck(form, type);
  if (check.status === 'ok') return true;
  if (!ack) return false;
  return ack.status === check.status
    && Math.abs(Number(ack.lineSum) - check.lineSum) < 0.001
    && Math.abs(Number(ack.docTotal) - check.docTotal) < 0.001;
}

export function hasValidLines(form, type) {
  return (form.lines || []).some((line) => lineIsValid(line));
}

export function paperStepValid(form, type) {
  return !paperStepError(form, type);
}

export function paperStepError(form, type, t) {
  const isOutgoing = type === 'INVOICE' && form.invoice.direction === 'OUTGOING';
  if (!String(form.counterpartyName || '').trim()) {
    return t ? (isOutgoing ? t('finance.stepErrors.client') : t('finance.stepErrors.partner')) : 'partner';
  }
  if (!form.documentDate) return t ? t('finance.stepErrors.date') : 'date';
  if (type === 'QUOTE' && (form.quote.totalAmount === '' || form.quote.totalAmount == null)) {
    return t ? t('finance.stepErrors.total') : 'total';
  }
  if (type === 'INVOICE') {
    if (!form.invoice.dueDate) return t ? t('finance.stepErrors.dueDate') : 'dueDate';
    if (form.invoice.totalAmount === '' || form.invoice.totalAmount == null) {
      return t ? t('finance.stepErrors.total') : 'total';
    }
    if (!form.attachmentFile) return t ? t('finance.originalRequired') : 'original';
  }
  return '';
}

export function stepValidationMessage(form, type, step, t) {
  if (type === 'INVOICE' && step === 0 && !form.invoice.direction) return t('finance.stepErrors.direction');
  if (type === 'INVOICE' && step === 1 && !form.invoice.categoryId) return t('finance.stepErrors.category');
  const paperIndex = type === 'INVOICE' ? 2 : 0;
  if (step === paperIndex) return paperStepError(form, type, t);
  const linesIndex = type === 'INVOICE' ? 3 : 1;
  if (step === linesIndex && !hasValidLines(form, type)) return t('finance.linesRequiredError');
  return '';
}

export function buildFinancePayload(form, type, { confirmDuplicate, isOutgoing }) {
  const lines = form.lines.filter((line) => String(line.description || '').trim()).map((line) => {
    const allocations = [];
    if (type === 'DELIVERY_NOTE' && line.costKind === 'WAREHOUSE') {
      allocations.push({ locationKind: 'WAREHOUSE', quantity: line.quantity || null, amount: line.totalAmount || null });
    } else if (line.projectId) {
      allocations.push({
        locationKind: 'PROJECT',
        projectId: Number(line.projectId),
        quantity: line.quantity || null,
        amount: line.totalAmount || null,
      });
    } else {
      allocations.push({
        locationKind: 'UNASSIGNED',
        explicitlyUnassigned: true,
        quantity: line.quantity || null,
        amount: line.totalAmount || null,
      });
    }
    return {
      description: line.description,
      quantity: line.quantity === '' ? null : Number(line.quantity),
      unit: line.unit,
      unitPrice: line.unitPrice === '' ? null : Number(line.unitPrice),
      totalAmount: line.totalAmount === '' ? null : Number(line.totalAmount),
      allocations,
    };
  });
  const hasUnassigned = lines.some((line) => (line.allocations || []).some((a) => a.locationKind === 'UNASSIGNED'));
  const payload = {
    type,
    documentNumber: form.documentNumber || undefined,
    documentDate: form.documentDate,
    companyId: isOutgoing ? undefined : (form.companyId || undefined),
    counterpartyName: form.counterpartyName,
    currency: form.currency,
    notes: form.notes || undefined,
    confirmDuplicate,
    confirmUnassigned: form.confirmUnassigned || hasUnassigned,
    lines,
    allocations: [],
    relations: (form.relatedDocuments || []).filter((doc) => doc?.id).map((doc) => ({ documentId: doc.id })),
  };
  if (type === 'QUOTE') {
    payload.quote = {
      ...form.quote,
      totalAmount: Number(form.quote.totalAmount),
      netAmount: form.quote.netAmount === '' ? null : Number(form.quote.netAmount),
      taxAmount: form.quote.taxAmount === '' ? null : Number(form.quote.taxAmount),
    };
  }
  if (type === 'DELIVERY_NOTE') {
    payload.deliveryNote = { receivedAt: form.deliveryNote.receivedAt || form.documentDate };
  }
  if (type === 'INVOICE') {
    payload.invoice = {
      ...form.invoice,
      categoryId: Number(form.invoice.categoryId),
      subcategoryId: form.invoice.subcategoryId ? Number(form.invoice.subcategoryId) : null,
      totalAmount: Number(form.invoice.totalAmount),
      netAmount: form.invoice.netAmount === '' ? null : Number(form.invoice.netAmount),
      taxAmount: form.invoice.taxAmount === '' ? null : Number(form.invoice.taxAmount),
    };
    payload.paid = form.paid;
  }
  return payload;
}

export default function FinanceCreateWizard({
  t,
  type,
  user,
  form,
  setForm,
  step,
  error,
  duplicates,
  companies,
  projects,
  categories,
  canPrices,
  linkSearchType,
  setLinkSearchType,
  linkOptions,
  searchRelated,
  workQuantities,
  addLine,
  addWorkQuantityLine,
}) {
  const isOutgoing = type === 'INVOICE' && form.invoice.direction === 'OUTGOING';
  const parents = categories.filter((c) => !c.parentId && c.active);
  const children = categories.filter((c) => c.parentId === Number(form.invoice.categoryId) && c.active);
  const paperIndex = type === 'INVOICE' ? 2 : 0;
  const linesIndex = type === 'INVOICE' ? 3 : 1;
  const linkIndex = type === 'INVOICE' ? 4 : 2;

  const steps = type === 'INVOICE'
    ? [t('finance.steps.direction'), t('finance.steps.category'), t('finance.steps.paper'), t('finance.steps.lines'), t('finance.steps.links')]
    : [t('finance.steps.paper'), t('finance.steps.lines'), t('finance.steps.links')];

  const updateLine = (idx, patch) => {
    setForm((p) => {
      const lines = [...p.lines];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...p, lines };
    });
  };

  return (
    <Stack spacing={2} mt={1}>
      <Stepper activeStep={step} alternativeLabel sx={{ mb: 1 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>
      {error && <Alert severity="warning">{error}</Alert>}
      {duplicates.map((dup) => (
        <Alert key={dup.id} severity="info">
          {dup.documentNumber} — {dup.counterpartyName}
        </Alert>
      ))}

      {type === 'INVOICE' && step === 0 && (
        <>
          <Typography variant="body2" color="text.secondary">{t('finance.hints.direction')}</Typography>
          <FormControl fullWidth>
            <InputLabel>{t('finance.direction')}</InputLabel>
            <Select
              label={t('finance.direction')}
              value={form.invoice.direction}
              onChange={(e) => {
                const direction = e.target.value;
                setForm((p) => ({
                  ...p,
                  companyId: direction === 'OUTGOING' ? null : p.companyId,
                  invoice: { ...p.invoice, direction },
                  lines: direction === 'OUTGOING'
                    ? p.lines.map((line) => ({ ...line, costKind: line.costKind === 'WAREHOUSE' ? 'DIRECT' : line.costKind }))
                    : p.lines,
                }));
              }}
            >
              <MenuItem value="INCOMING">{t('finance.incoming')}</MenuItem>
              <MenuItem value="OUTGOING">{t('finance.outgoing')}</MenuItem>
            </Select>
          </FormControl>
        </>
      )}

      {type === 'INVOICE' && step === 1 && (
        <>
          <Typography variant="body2" color="text.secondary">{t('finance.hints.category')}</Typography>
          <FormControl fullWidth>
            <InputLabel>{t('finance.category')}</InputLabel>
            <Select label={t('finance.category')} value={form.invoice.categoryId} onChange={(e) => setForm((p) => ({ ...p, invoice: { ...p.invoice, categoryId: e.target.value, subcategoryId: '' } }))}>
              {parents.filter((c) => c.appliesTo === 'BOTH' || c.appliesTo === form.invoice.direction).map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {children.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>{t('finance.subcategory')}</InputLabel>
              <Select label={t('finance.subcategory')} value={form.invoice.subcategoryId} onChange={(e) => setForm((p) => ({ ...p, invoice: { ...p.invoice, subcategoryId: e.target.value } }))}>
                {children.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {parents.find((c) => c.id === Number(form.invoice.categoryId))?.tooltip && (
            <Typography variant="caption">{parents.find((c) => c.id === Number(form.invoice.categoryId)).tooltip}</Typography>
          )}
        </>
      )}

      {step === paperIndex && (
        <>
          <Typography variant="body2" color="text.secondary">{t('finance.hints.paper')}</Typography>
          {isOutgoing ? (
            <TextField
              label={t('finance.client')}
              value={form.counterpartyName}
              onChange={(e) => setForm((p) => ({ ...p, counterpartyName: e.target.value, companyId: null }))}
              helperText={t('finance.clientHint')}
            />
          ) : (
            <>
              <Autocomplete
                options={companies}
                getOptionLabel={(opt) => opt.name || ''}
                onChange={(_, value) => setForm((p) => ({ ...p, companyId: value?.id || null, counterpartyName: value?.name || p.counterpartyName }))}
                renderInput={(params) => <TextField {...params} label={t('finance.partner')} />}
                freeSolo
                inputValue={form.counterpartyName}
                onInputChange={(_, value) => setForm((p) => ({ ...p, counterpartyName: value, companyId: p.companyId && companies.find((c) => c.name === value)?.id || null }))}
              />
              {!form.companyId && form.counterpartyName && canAccess(user, 'companies.write') && (
                <Alert severity="info">{t('finance.suggestAddCompany')}</Alert>
              )}
            </>
          )}
          <TextField label={t('finance.number')} value={form.documentNumber} onChange={(e) => setForm((p) => ({ ...p, documentNumber: e.target.value }))} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <DateInputWithArrows fullWidth margin="none" label={t('finance.date')} value={form.documentDate} onChange={(e) => setForm((p) => ({ ...p, documentDate: e.target.value }))} />
            </Grid>
            {type === 'QUOTE' && (
              <Grid item xs={12} md={6}>
                <DateInputWithArrows fullWidth margin="none" label={t('finance.validUntil')} value={form.quote.validUntil} onChange={(e) => setForm((p) => ({ ...p, quote: { ...p.quote, validUntil: e.target.value } }))} />
              </Grid>
            )}
            {type === 'DELIVERY_NOTE' && (
              <Grid item xs={12} md={6}>
                <DateInputWithArrows fullWidth margin="none" label={t('finance.deliveryDate')} value={form.deliveryNote.receivedAt} onChange={(e) => setForm((p) => ({ ...p, deliveryNote: { receivedAt: e.target.value } }))} />
              </Grid>
            )}
            {type === 'INVOICE' && (
              <Grid item xs={12} md={6}>
                <DateInputWithArrows fullWidth margin="none" label={t('finance.dueDate')} value={form.invoice.dueDate} onChange={(e) => setForm((p) => ({ ...p, invoice: { ...p.invoice, dueDate: e.target.value } }))} />
              </Grid>
            )}
          </Grid>
          {type === 'QUOTE' && (
            <>
              <TextField type="number" label={t('finance.total')} value={form.quote.totalAmount} onChange={(e) => setForm((p) => ({ ...p, quote: { ...p.quote, totalAmount: e.target.value } }))} />
              <FormControl fullWidth>
                <InputLabel>{t('finance.status')}</InputLabel>
                <Select label={t('finance.status')} value={form.quote.status} onChange={(e) => setForm((p) => ({ ...p, quote: { ...p.quote, status: e.target.value } }))}>
                  {['DRAFT', 'ACTIVE', 'ACCEPTED', 'REJECTED', 'CANCELLED', 'CLOSED'].map((s) => <MenuItem key={s} value={s}>{t(`finance.quoteStatus.${s}`)}</MenuItem>)}
                </Select>
              </FormControl>
            </>
          )}
          {type === 'INVOICE' && (
            <>
              <TextField type="number" label={t('finance.total')} value={form.invoice.totalAmount} onChange={(e) => setForm((p) => ({ ...p, invoice: { ...p.invoice, totalAmount: e.target.value } }))} />
              <FormControlLabel control={<Checkbox checked={form.paid} onChange={(e) => setForm((p) => ({ ...p, paid: e.target.checked }))} />} label={t('finance.paid')} />
            </>
          )}
          <Button component="label" variant={type === 'INVOICE' && !form.attachmentFile ? 'contained' : 'outlined'} color={type === 'INVOICE' && !form.attachmentFile ? 'warning' : 'primary'}>
            {form.attachmentFile ? form.attachmentFile.name : t('finance.original')}
            <input hidden type="file" accept=".pdf,image/jpeg,image/png,.jpg,.jpeg,.png" onChange={(e) => setForm((p) => ({ ...p, attachmentFile: e.target.files?.[0] || null }))} />
          </Button>
          {type === 'INVOICE' && !form.attachmentFile && (
            <FormHelperText error>{t('finance.originalRequired')}</FormHelperText>
          )}
        </>
      )}

      {step === linesIndex && (
        <>
          <Typography variant="body2" color="text.secondary">{t('finance.hints.linesRequired')}</Typography>
          {isOutgoing && (
            <Box>
              <Typography variant="subtitle2">{t('finance.workQuantitySuggestions')}</Typography>
              {workQuantities.length === 0 && (
                <Typography variant="body2" color="text.secondary">{t('finance.noWorkQuantities')}</Typography>
              )}
              <Stack spacing={1}>
                {workQuantities.map((row) => (
                  <Button key={row.id} size="small" variant="outlined" onClick={() => addWorkQuantityLine(row)}>
                    {(row.costSheet?.name || row.costSheet?.code || `#${row.id}`)} · {row.quantity} {row.costSheet?.unit || ''}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
          <Button onClick={() => addLine()}>{t('finance.addLine')}</Button>
          {form.lines.map((line, idx) => (
            <Grid container spacing={1} key={idx} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField fullWidth label={t('finance.description')} value={line.description} onChange={(e) => updateLine(idx, { description: e.target.value })} />
              </Grid>
              <Grid item xs={6} md={1.5}>
                <TextField fullWidth type="number" label={t('finance.quantity')} value={line.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} />
              </Grid>
              <Grid item xs={6} md={1.5}>
                <FormControl fullWidth>
                  <InputLabel>{t('finance.unit')}</InputLabel>
                  <Select
                    label={t('finance.unit')}
                    value={line.unit || UNITS.UNIT}
                    onChange={(e) => updateLine(idx, { unit: e.target.value })}
                  >
                    {unitSelectOptions(line.unit).map((unit) => (
                      <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {canPrices && type !== 'DELIVERY_NOTE' && (
                <Grid item xs={12} md={1.5}>
                  <TextField fullWidth type="number" label={t('finance.total')} value={line.totalAmount} onChange={(e) => updateLine(idx, { totalAmount: e.target.value })} />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                {type === 'DELIVERY_NOTE' ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={line.costKind === 'WAREHOUSE' ? 'WAREHOUSE' : 'PROJECT'}
                      onChange={(_, value) => {
                        if (!value) return;
                        updateLine(idx, { costKind: value, projectId: value === 'WAREHOUSE' ? '' : line.projectId });
                      }}
                    >
                      <ToggleButton value="PROJECT">{t('finance.project')}</ToggleButton>
                      <ToggleButton value="WAREHOUSE">{t('finance.warehouse')}</ToggleButton>
                    </ToggleButtonGroup>
                    {line.costKind !== 'WAREHOUSE' && (
                      <Select size="small" value={line.projectId || ''} onChange={(e) => updateLine(idx, { projectId: e.target.value, costKind: 'PROJECT' })} displayEmpty sx={{ minWidth: 140 }}>
                        <MenuItem value="">{t('finance.project')}</MenuItem>
                        {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                      </Select>
                    )}
                  </Stack>
                ) : (
                  <Select size="small" value={line.projectId || ''} onChange={(e) => updateLine(idx, { projectId: e.target.value })} displayEmpty sx={{ minWidth: 140 }}>
                    <MenuItem value="">{t('finance.notProjectRelated')}</MenuItem>
                    {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </Select>
                )}
              </Grid>
            </Grid>
          ))}
          {!hasValidLines(form, type) && (
            <FormHelperText error>{t('finance.linesRequiredError')}</FormHelperText>
          )}
        </>
      )}

      {step === linkIndex && (
        <>
          <Typography variant="body2" color="text.secondary">{t('finance.hints.links')}</Typography>
          <FormControl fullWidth>
            <InputLabel>{t('finance.linkType')}</InputLabel>
            <Select label={t('finance.linkType')} value={linkSearchType} onChange={(e) => setLinkSearchType(e.target.value)}>
              {TYPES.filter((item) => item.type !== type && canAccess(user, item.read)).map((item) => (
                <MenuItem key={item.type} value={item.type}>{t(`finance.types.${item.type}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            options={linkOptions.filter((opt) => !(form.relatedDocuments || []).some((doc) => doc.id === opt.id))}
            getOptionLabel={documentLabel}
            value={null}
            onChange={(_, value) => {
              if (value) {
                setForm((p) => ({
                  ...p,
                  relatedDocuments: [...(p.relatedDocuments || []).filter((doc) => doc.id !== value.id), value],
                }));
              }
            }}
            onInputChange={(_, value) => { searchRelated(value).catch(() => {}); }}
            renderInput={(params) => <TextField {...params} label={t('finance.findDocument')} helperText={t('finance.linkHint')} />}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(form.relatedDocuments || []).map((doc) => (
              <Chip
                key={doc.id}
                label={documentLabel(doc)}
                onDelete={() => setForm((p) => ({ ...p, relatedDocuments: (p.relatedDocuments || []).filter((item) => item.id !== doc.id) }))}
              />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}
